// src/pages/Admin.js
import React, { useState, useEffect } from 'react';
import { db, auth } from '../lib/firebase';
import {
  collection, getDocs, query, orderBy,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { createMerchant } from '../lib/MerchantModel';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Plus, X, LayoutDashboard } from 'lucide-react';

const PLAN_COLORS = {
  silver:   { bg: '#f5f5f5',             color: '#555' },
  gold:     { bg: '#fefce8',             color: '#ca8a04' },
  platinum: { bg: 'rgba(64,0,228,0.06)', color: '#4000E4' },
};

const inputStyle = {
  width: '100%', padding: '10px 14px',
  border: '1px solid rgba(0,0,0,0.1)', borderRadius: '10px',
  fontFamily: "'Outfit', sans-serif", fontSize: '14px',
  color: '#1a1a1a', outline: 'none', background: '#fff',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: '11px', fontWeight: 600,
  letterSpacing: '0.1em', textTransform: 'uppercase',
  color: '#888', marginBottom: '5px',
};

export default function Admin() {
  const { user, isAdmin, adminMerchant, setAdminMerchant, signOut } = useAuth();
  const navigate = useNavigate();

  const [merchants, setMerchants]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showCreate, setShowCreate]       = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(false);
  const [form, setForm]                   = useState({ name: '', email: '', password: '', address: '', phone: '' });
  const [creating, setCreating]           = useState(false);
  const [createError, setCreateError]     = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin && !loading) navigate('/dashboard');
  }, [isAdmin, loading, navigate]);

  // Navigate only after adminMerchant is confirmed set in context
  useEffect(() => {
    if (pendingNavigate && adminMerchant) {
      setPendingNavigate(false);
      navigate('/dashboard');
    }
  }, [adminMerchant, pendingNavigate, navigate]);

  const loadMerchants = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'merchants'), orderBy('createdAt', 'desc')));
    setMerchants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { loadMerchants(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await createMerchant(cred.user.uid, {
        name: form.name, email: form.email,
        phone: form.phone || null, address: form.address || null,
      });
      setCreateSuccess(`✓ Merchant "${form.name}" created. They can now sign in with the password you set.`);
      setForm({ name: '', email: '', password: '', address: '', phone: '' });
      loadMerchants();
    } catch (err) {
      setCreateError(err.message.replace('Firebase: ', '').replace(/\(auth.*\)/, '').trim());
    }
    setCreating(false);
  };

  const handleViewDashboard = (m) => {
    setAdminMerchant(m);
    setPendingNavigate(true);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F7F4EF', fontFamily: "'Outfit', sans-serif", padding: '32px 24px' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;1,400&family=Outfit:wght@400;500;600;700&display=swap');`}</style>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: '#1a1a1a' }}>Admin Panel</div>
            <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>Signed in as <strong>{user?.email}</strong></div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowCreate(!showCreate)}
              style={{ padding: '10px 20px', background: '#4000E4', color: '#fff', border: 'none', borderRadius: '10px', fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}
            >
              <Plus size={15} /> Create Merchant
            </button>
            <button
              onClick={async () => { await signOut(); navigate('/signin'); }}
              style={{ padding: '10px 20px', background: 'transparent', color: '#888', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '10px', fontFamily: "'Outfit', sans-serif", fontSize: '13px', cursor: 'pointer' }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Create form */}
        {showCreate && (
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', border: '1px solid rgba(64,0,228,0.2)', marginBottom: '24px', boxShadow: '0 4px 24px rgba(64,0,228,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>New Merchant</div>
              <button onClick={() => { setShowCreate(false); setCreateError(''); setCreateSuccess(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa' }}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>Restaurant Name *</label>
                  <input style={inputStyle} required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Rosso Osteria" />
                </div>
                <div>
                  <label style={labelStyle}>Login Email *</label>
                  <input style={inputStyle} type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="owner@restaurant.com" />
                </div>
                <div>
                  <label style={labelStyle}>Temporary Password *</label>
                  <input style={inputStyle} type="password" required minLength={6} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 6 characters" />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input style={inputStyle} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 000-0000" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Address</label>
                  <input style={inputStyle} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St, Los Angeles, CA" />
                </div>
              </div>
              {createError   && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#dc2626', marginBottom: '14px' }}>{createError}</div>}
              {createSuccess && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#16a34a', marginBottom: '14px' }}>{createSuccess}</div>}
              <button type="submit" disabled={creating} style={{ padding: '10px 24px', background: '#4000E4', color: '#fff', border: 'none', borderRadius: '10px', fontFamily: "'Outfit', sans-serif", fontSize: '14px', fontWeight: 600, cursor: 'pointer', opacity: creating ? 0.6 : 1 }}>
                {creating ? 'Creating...' : 'Create Merchant'}
              </button>
            </form>
          </div>
        )}

        {/* Merchants list */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>All Merchants</div>
            <div style={{ fontSize: '13px', color: '#aaa' }}>{merchants.length} total</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 90px 100px 44px', gap: '12px', padding: '10px 24px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
            {['Restaurant', 'Email', 'Plan', 'Created', ''].map(h => (
              <div key={h} style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa' }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>Loading...</div>
          ) : merchants.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>No merchants yet.</div>
          ) : (
            merchants.map((m, i) => {
              const planStyle = PLAN_COLORS[m.plan] || PLAN_COLORS.silver;
              const date = m.createdAt?.toDate?.();
              return (
                <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 90px 100px 44px', gap: '12px', padding: '14px 24px', alignItems: 'center', borderBottom: i < merchants.length - 1 ? '1px solid #fafafa' : 'none' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{m.name || '—'}</div>
                    <div style={{ fontSize: '11px', color: '#bbb', marginTop: '2px' }}>{m.id}</div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email || '—'}</div>
                  <div>
                    <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '20px', background: planStyle.bg, color: planStyle.color }}>
                      {m.plan || 'silver'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa' }}>
                    {date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                  </div>
                  <div>
                    <button
                      title="View dashboard"
                      onClick={() => handleViewDashboard(m)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4000E4', display: 'flex', alignItems: 'center' }}
                    >
                      <LayoutDashboard size={15} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}